// The VR thumbstick gate (w57, owner 2026-11-03; APP_PLAYER "VR controls" →
// "Stick gate"): an UNTOUCHED stick never moves, turns or steers anything.
// Pure (no THREE, no XR): VRControls.poll (player.js) runs one per hand over
// the raw xr-standard thumbstick (axes[2] / axes[3]) and its button
// (buttons[3]).
//
// Why: the owner's right stick rests at x ≈ 0.96 (w54, measured untouched) —
// far past any dead zone, so a stick-steered jet would spin. Two standard
// facts make it fixable without a calibration screen:
//   • WebXR Gamepads Module, the xr-standard mapping: buttons[3] = the
//     primary thumbstick, axes[2] / axes[3] = its X / Y
//     (https://www.w3.org/TR/webxr-gamepads-module-1/ §xr-standard).
//   • Gamepad, GamepadButton.touched: with a touch sensor it is true while
//     the control is touched; WITHOUT one it mirrors pressed
//     (https://www.w3.org/TR/gamepad/ "touched"). The Quest Touch
//     controllers' thumbsticks are capacitive.
// So: a stick reported touched while NOT pressed has a sensor (`cap`). Then
//   • untouched → output 0, and after STICK_SETTLE_S (the spring-back) the
//     reading is the stick's REST OFFSET (an EMA, STICK_REST_TAU_S);
//   • touched → the reading minus that rest, rescaled per side so a full
//     deflection still reads ±1 (v ≥ r: (v − r)/(1 − r), else (v − r)/(1 + r)),
//     the divisor never under REMAP_MIN: a rest near the rim (a stuck stick)
//     never turns sensor noise into a full deflection — that side stays dead.
// A stick that MOVES (a span over STICK_NOCAP_SPAN within one untouched run)
// while never touched has no sensor (`nocap`: touched only mirrors the
// click): raw axes from then on (the callers' dead zones as before). Until
// either is known the gate assumes a sensor (an untouched stick reads 0):
// the drifting stick at rest — the case that spins — is held from the first
// frame, and a sensor-less stick is released at its first real push.

export const STICK_SETTLE_S = 0.2;     // untouched this long before the reading counts as the rest
export const STICK_REST_TAU_S = 0.5;   // the rest offset's EMA time constant
export const STICK_NOCAP_SPAN = 0.5;   // an untouched run moving this far = no touch sensor
const REST_MAX = 0.98;                 // a rest clamp (a pinned stick)
const REMAP_MIN = 0.5;                 // the per-side divisor's floor (no noise amplification)

function remap(v, r) {
  const d = v - r;
  const o = d / Math.max(REMAP_MIN, d >= 0 ? 1 - r : 1 + r);
  return Math.max(-1, Math.min(1, o));
}

export class StickGate {
  constructor() { this.reset(); }

  // a controller woke / changed (VRControls.sourcesChanged): learn afresh
  reset() {
    this.cap = false;       // a touch sensor seen (touched && !pressed)
    this.nocap = false;     // moved untouched: no sensor, raw axes
    this.restX = 0; this.restY = 0;
    this.restN = 0;         // rest samples taken (0 = no rest known)
    this._untT = 0;         // seconds untouched in this run
    this._minX = 0; this._maxX = 0; this._minY = 0; this._maxY = 0;
    this.x = 0; this.y = 0;
    this.touched = false;
  }

  // ax, ay: the raw axes; btn: gamepad.buttons[3] (may be undefined); dt in s.
  // Returns this ({ x, y, touched }), refilled (no allocation).
  update(ax, ay, btn, dt) {
    const pressed = !!(btn && btn.pressed);
    const touched = !!(btn && (btn.touched || btn.pressed));
    if (btn && btn.touched && !pressed) { this.cap = true; this.nocap = false; }
    this.touched = touched;
    if (this.nocap || !btn) { this.x = ax; this.y = ay; return this; }
    if (touched) {
      this._untT = 0;
      this.x = remap(ax, this.restX); this.y = remap(ay, this.restY);
      return this;
    }
    // untouched: nothing moves
    this.x = 0; this.y = 0;
    if (this._untT === 0) { this._minX = this._maxX = ax; this._minY = this._maxY = ay; }
    this._untT += dt;
    this._minX = Math.min(this._minX, ax); this._maxX = Math.max(this._maxX, ax);
    this._minY = Math.min(this._minY, ay); this._maxY = Math.max(this._maxY, ay);
    if (!this.cap && Math.max(this._maxX - this._minX, this._maxY - this._minY) > STICK_NOCAP_SPAN) {
      // a pushed stick that never reported a touch: no sensor — raw from now on
      this.nocap = true; this.restX = this.restY = 0; this.restN = 0;
      this.x = ax; this.y = ay;
      return this;
    }
    if (this._untT >= STICK_SETTLE_S) {
      const cx = Math.max(-REST_MAX, Math.min(REST_MAX, ax));
      const cy = Math.max(-REST_MAX, Math.min(REST_MAX, ay));
      if (this.restN === 0) { this.restX = cx; this.restY = cy; }
      else {
        const k = 1 - Math.exp(-dt / STICK_REST_TAU_S);
        this.restX += (cx - this.restX) * k; this.restY += (cy - this.restY) * k;
      }
      this.restN++;
    }
    return this;
  }
}
